import { type RefObject, useState } from 'react';
import useResizeObserver from 'use-resize-observer';

import useDebouncedCallback from './useDebouncedCallback';

/**
 * A debounced version of useResizeObserver that helps prevent ResizeObserver loop errors
 * by debouncing the resize notifications.
 *
 * @template T - The type of HTML element being observed
 * @param ref - Optional ref to use instead of creating a new one
 * @param debounceMs - The debounce delay in milliseconds (default: 100)
 * @returns Object containing width, height, and ref
 */
export function useDebouncedResizeObserver<T extends HTMLElement = HTMLDivElement>(
  ref?: RefObject<T>,
  debounceMs: number = 50,
) {
  const [dimensions, setDimensions] = useState<{ width?: number; height?: number }>({});

  const debouncedResize = useDebouncedCallback(({ width, height }: { width?: number; height?: number }) => {
    //  Only update state when the measured size actually changes. Always
    //  returning a fresh object would re-render on every observer callback even
    //  when the size is unchanged; for consumers that re-run a layout-affecting
    //  effect on each render (e.g. the snippets tree's auto-expansion), that
    //  feeds back into the observer and the browser reports a benign
    //  "ResizeObserver loop completed with undelivered notifications" (which the
    //  dev-server overlay surfaces as a fatal error).
    setDimensions((prev) =>
      (prev.width === width && prev.height === height ? prev : { width, height }));
  }, debounceMs);

  const { ref: resizeRef } = useResizeObserver<T>({
    ref,
    onResize: debouncedResize,
  });

  return {
    ...dimensions,
    ref: ref || resizeRef,
  };
}
